import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  InlineMessage,
  MenuDragAndDrop,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import { CF_SEC_BLD } from '../../../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../../../shared/hooks';
import { type EntitlementColumnState } from '../../../../../../ViolationsEntitlement/data-layer';

/**
 * UsersColumnList Component
 *
 * Drag-and-drop interface for customizing column visibility and ordering in the entitlement
 * users list with search functionality, validation limits, and user feedback for
 * optimal column management experience.
 *
 * @component
 * @param {EntitlementColumnState[]} columns - Array of column configuration objects
 * @param {boolean} isDisabled - Whether the column picker is disabled
 * @param {function} handleColumnsChange - Callback function for column state changes
 * @returns {React.ReactElement} Column picker with drag-and-drop interface including:
 *   - Drag and drop column reordering
 *   - Column visibility toggles
 *   - Maximum 5 columns validation
 *   - Search functionality
 *   - Badge indicator for active columns
 *
 * @example
 * <UsersColumnList
 *   columns={columnState}
 *   handleColumnsChange={handleChange}
 *   isDisabled={loading}
 * />
 *
 * @dependencies
 * - Hooks: useInvertedLocalize for localization
 * - Components: MenuDragAndDrop, ButtonSelect, Badge, InlineMessage
 */
interface UsersColumnListProps {
  columns: EntitlementColumnState[];
  isDisabled?: boolean;
  handleColumnsChange?: (columns: EntitlementColumnState[]) => void;
}

function UsersColumnList({
  columns,
  isDisabled,
  handleColumnsChange,
}: UsersColumnListProps): ReactElement {
  const l = useInvertedLocalize();
  const [options, setOptions] = useState(columns);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (handleColumnsChange) {
      handleColumnsChange(options);
    }
  }, [options, handleColumnsChange]);

  useEffect(() => {
    if (isDisabled) {
      setIsExpanded(false);
    }
  }, [isDisabled]);

  const countActiveColumns = useCallback(
    (menuColumns: EntitlementColumnState[]): number =>
      menuColumns.reduce(
        (count, option) => (option.isChecked ? count + 1 : count),
        0
      ),
    []
  );

  const currentCount = countActiveColumns(options);

  const renderedMenuTrigger = (
    <ButtonSelect
      kind='rectangle'
      color={currentCount ? 'neutral' : 'transparent'}
      label={l('List')}
      ariaLabel={l('List')}
      size='small'
      isDisabled={isDisabled ?? options.length === 0}
      prefixIcon={<Icon kind='Edit' />}
      isVisualCueEnabled={false}
      BadgeComp={
        currentCount ? <Badge kind='dot' color='primary' size='small' /> : null
      }
    />
  );

  const detailsPanel = (
    <Tooltip
      trigger={
        <Typography kind='body3' color={CF_SEC_BLD}>
          {l('details panel')}
        </Typography>
      }
      text={l('Tap anywhere on the row to open the details panel.')}
      placement='bottom-start'
    />
  );

  const updateColumns = useCallback(
    (menuColumns: EntitlementColumnState[], activeCount: number) =>
      menuColumns.map((item) => {
        if (activeCount >= 5 && !item.isChecked) {
          return { ...item, isDisabled: true };
        }

        if (activeCount < 5 && item.isDisabled) {
          return { ...item, isDisabled: false };
        }

        return item;
      }),
    []
  );

  const modifiedSetOptions = useCallback(
    (optionsToModify: EntitlementColumnState[]) => {
      const activeColumnCount = countActiveColumns(optionsToModify);

      const updatedColumns = updateColumns(optionsToModify, activeColumnCount);

      setOptions(updatedColumns);
    },
    [countActiveColumns, updateColumns]
  );

  const [columnsLoaded, setColumnsLoaded] = useState(false);

  useEffect(() => {
    if (columns.length > 0 && !columnsLoaded) {
      modifiedSetOptions(columns);
      setColumnsLoaded(true);
    }
  }, [columns, columnsLoaded, modifiedSetOptions]);

  const labelText = l(
    'The number of visible attributes depends on the size of your browser. Any attributes not shown are visible in the'
  );
  const maxColumnsSelected = l(
    "You've reached maximum number of columns sets allowed. Remove existing ones to add more."
  );
  const label =
    countActiveColumns(options) >= 5 ? (
      <InlineMessage
        kind='background'
        text={maxColumnsSelected}
        colorTheme='info'
        size='medium'
        isVisible
        isDismissible={false}
        leftIcon={<Icon kind='AlertInfoFilled' />}
      />
    ) : (
      <span>
        {labelText} {detailsPanel}
      </span>
    );

  return (
    <Box className='SodColumnsList'>
      <MenuDragAndDrop
        label={label}
        options={options}
        setOptions={modifiedSetOptions}
        trigger={renderedMenuTrigger}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
        minOptionsForSearch={options.length + 1}
        onChange={() => {}}
      />
    </Box>
  );
}

UsersColumnList.defaultProps = {
  isDisabled: false,
  handleColumnsChange: () => {},
};

export default memo(UsersColumnList);
